import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { rawMaterialService } from '@services/rawMaterialService';
import { CreateRawMaterialRequest, MATERIAL_TYPE_OPTIONS, UNIT_OPTIONS } from '@/types/rawMaterial';

/**
 * Formatação do NCM conforme padrão XXXX.XX.XX 
 */
const formatNCM = (value: string): string => {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 4) return clean;
  if (clean.length <= 6) return `${clean.slice(0,4)}.${clean.slice(4)}`;
  return `${clean.slice(0,4)}.${clean.slice(4,6)}.${clean.slice(6,8)}`;
};

/**
 * Formulário de matéria-prima para criação e edição
 */
export const RawMaterialFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeValidated, setCodeValidated] = useState(false);
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  
  // Dados do formulário
  const [formData, setFormData] = useState<CreateRawMaterialRequest>({
    code: '',
    description: '',
    unit: 'UN',
    material_type: 0,
    cost: 0,
    total_weight: 0,
    stock_quantity_available: 0,
    min_stock_quantity: 0,
    max_stock_quantity: 0,
    ncm: '',
    notes: '',
    is_active: true,
  });

  // Validação do formulário
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Carrega material para edição
  useEffect(() => {
    if (isEditing && id) {
      loadMaterial(id);
    }
  }, [id, isEditing]);

  // Validação do código com debounce
  useEffect(() => {
    if (isEditing || !formData.code.trim()) {
      setCodeValidated(false);
      setCodeError(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setCodeValidating(true);
        setCodeError(null);
        const result = await rawMaterialService.validateCode(formData.code.trim());
        setCodeValidated(result.available);
        if (!result.available) {
          setCodeError(result.message);
        }
      } catch (err) {
        setCodeError('Erro ao validar código');
        setCodeValidated(false);
      } finally {
        setCodeValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.code, isEditing]);

  const loadMaterial = async (materialId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const material = await rawMaterialService.getRawMaterialById(parseInt(materialId));
      
      setFormData({
        code: material.code,
        description: material.description,
        unit: material.unit || 'UN',
        material_type: material.material_type,
        cost: material.cost || 0,
        total_weight: material.total_weight || 0,
        stock_quantity_available: material.stock_quantity_available || 0,
        min_stock_quantity: material.min_stock_quantity || 0,
        max_stock_quantity: material.max_stock_quantity || 0,
        ncm: material.ncm || '',
        notes: material.notes || '',
        is_active: material.is_active !== false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar matéria-prima');
    } finally {
      setLoading(false);
    }
  };

  // Atualiza campo do formulário
  const updateField = (field: keyof CreateRawMaterialRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validação dos campos
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Código é obrigatório';
    } else if (formData.code.length > 50) {
      newErrors.code = 'Código deve ter no máximo 50 caracteres';
    } else if (!codeValidated && !isEditing) {
      newErrors.code = 'Por favor, aguarde a validação do código';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    } else if (formData.description.length > 150) {
      newErrors.description = 'Descrição deve ter no máximo 150 caracteres';
    }
    
    if ((formData.cost || 0) < 0) {
      newErrors.cost = 'Custo não pode ser negativo';
    }
    
    if ((formData.min_stock_quantity || 0) < 0) {
      newErrors.min_stock_quantity = 'Estoque mínimo não pode ser negativo';
    }
    
    if ((formData.max_stock_quantity || 0) < 0) {
      newErrors.max_stock_quantity = 'Estoque máximo não pode ser negativo';
    }
    
    if ((formData.min_stock_quantity || 0) > (formData.max_stock_quantity || 0) && (formData.max_stock_quantity || 0) > 0) {
      newErrors.min_stock_quantity = 'Estoque mínimo deve ser menor que o máximo';
    }
    
    if ((formData.total_weight || 0) < 0) {
      newErrors.total_weight = 'Peso não pode ser negativo';
    }
    
    if (formData.ncm && !/^\d{4}\.\d{2}\.\d{2}$/.test(formData.ncm)) {
      newErrors.ncm = 'NCM deve seguir o formato XXXX.XX.XX (ex: 4202.11.00)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salva o material
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);
      
      if (isEditing && id) {
        const { stock_quantity_available, ...updateData } = formData;
        await rawMaterialService.updateRawMaterial(parseInt(id), updateData);
      } else {
        await rawMaterialService.createRawMaterial(formData);
      }
      
      navigate('/raw-materials');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar matéria-prima';
      
      if (errorMessage.toLowerCase().includes('código') && errorMessage.toLowerCase().includes('uso')) {
        setErrors(prev => ({ ...prev, code: errorMessage }));
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/raw-materials')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <div className="grid gap-6">
          <Card>
            <CardContent>
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/raw-materials')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Matéria-Prima' : 'Nova Matéria-Prima'}
          </h1>
          <p className="text-slate-600">
            {isEditing 
              ? 'Altere as informações da matéria-prima'
              : 'Preencha os dados para cadastrar uma nova matéria-prima'
            }
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <div className="grid gap-6">
        {/* Informações básicas */}
        <Card>
          <CardHeader title="Informações Básicas" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Código *"
                  placeholder="Ex: MP-001"
                  value={formData.code}
                  onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                  error={errors.code || codeError || undefined}
                  maxLength={50}
                  disabled={isEditing}
                />
                {codeValidating && (
                  <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                    <span className="animate-spin w-3 h-3 border border-blue-500 border-t-transparent rounded-full inline-block" />
                    Validando código...
                  </p>
                )}
                {codeValidated && !codeError && formData.code.trim() && (
                  <p className="text-xs text-green-600 mt-1">Código disponível</p>
                )}
              </div>
              
              <Input
                label="NCM"
                placeholder="Ex: 4202.11.00"
                value={formData.ncm}
                onChange={(e) => updateField('ncm', formatNCM(e.target.value))}
                error={errors.ncm}
                maxLength={10}
              />

              <div className="md:col-span-2">
                <Input
                  label="Descrição *"
                  placeholder="Ex: Couro Bovino Natural - Peça Inteira"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  error={errors.description}
                  maxLength={150}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Tipo de Material *
                </label>
                <select
                  value={formData.material_type}
                  onChange={(e) => updateField('material_type', parseInt(e.target.value))}
                  className="input"
                >
                  {MATERIAL_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unidade de Medida
                </label>
                <select
                  value={formData.unit || ''}
                  onChange={(e) => updateField('unit', e.target.value || undefined)}
                  className="input"
                >
                  <option value="">Selecione...</option>
                  {UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custo e Peso */}
        <Card>
          <CardHeader title="Custo e Peso" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Custo Unitário"
                type="number"
                placeholder="0,00"
                value={formData.cost}
                onChange={(e) => updateField('cost', parseFloat(e.target.value) || 0)}
                error={errors.cost}
                step={0.01}
                min={0}
              />
              
              <Input
                label="Peso Bruto (kg)"
                type="number"
                placeholder="0,000"
                value={formData.total_weight}
                onChange={(e) => updateField('total_weight', parseFloat(e.target.value) || 0)}
                step={0.001}
                min={0}
                error={errors.total_weight}
              />
            </div>
          </CardContent>
        </Card>

        {/* Estoque */}
        <Card>
          <CardHeader title="Controle de Estoque" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Estoque Disponível
                  </label>
                  <div className="px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-600">
                    {formData.stock_quantity_available}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Atualizado por outros processos</p>
                </div>
              ) : (
                <Input
                  label="Estoque Disponível"
                  type="number"
                  placeholder="0"
                  value={formData.stock_quantity_available}
                  onChange={(e) => updateField('stock_quantity_available', parseInt(e.target.value) || 0)}
                  min={0}
                />
              )}
              
              <Input
                label="Estoque Mínimo"
                type="number"
                placeholder="0"
                value={formData.min_stock_quantity}
                onChange={(e) => updateField('min_stock_quantity', parseInt(e.target.value) || 0)}
                error={errors.min_stock_quantity}
                min={0}
              />
              
              <Input
                label="Estoque Máximo"
                type="number"
                placeholder="0"
                value={formData.max_stock_quantity}
                onChange={(e) => updateField('max_stock_quantity', parseInt(e.target.value) || 0)}
                error={errors.max_stock_quantity}
                min={0}
              />
            </div>
          </CardContent>
        </Card>

        {/* Observações */}
        <Card>
          <CardHeader title="Observações" />
          <CardContent>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Observações / Notas
              </label>
              <textarea
                className="input w-full min-h-[100px] resize-y"
                placeholder="Informações adicionais sobre a matéria-prima..."
                value={formData.notes || ''}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Status */}
        <Card>
          <CardHeader title="Status" />
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                  className="rounded border-slate-300"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">
                  Material ativo
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/raw-materials')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            icon={<Save size={20} />}
          >
            {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Matéria-Prima'}
          </Button>
        </div>
      </div>
    </div>
  );
};
